import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Layout, Menu } from 'antd';
import {
  AreaChartOutlined,
  BarChartOutlined,
  CarOutlined,
  ClockCircleOutlined,
  CodeSandboxOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  DiffOutlined,
  DollarOutlined,
  HomeOutlined,
  PieChartOutlined,
  ShopOutlined,
  SolutionOutlined,
  TeamOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import { routePaths } from 'router';
import type { IMenuItem } from 'layouts/global-layout/components/sidebar/types.ts';
import { useGetItems } from 'layouts/global-layout/components/sidebar/use-get-items.ts';
import { UserRole } from 'types/user.ts';

export const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);

  const location = useLocation();
  const basePath = location.pathname.split('/')[1];

  const companyMenuItems: IMenuItem[] = [
    { path: routePaths.warehouses, label: 'Warehouses' },
    { path: routePaths.transports, label: 'Transports' },
    { path: routePaths.users, label: 'Users' },
    { path: routePaths.costsPerKm, label: 'Costs per km', allowedRoles: [UserRole.admin, UserRole.transport_manager] },
  ];

  const statisticsMenuItems: IMenuItem[] = [
    {
      path: routePaths.transportPerformance,
      label: 'Truck performance',
      icon: DashboardOutlined,
      allowedRoles: [UserRole.admin, UserRole.assistant],
    },
    {
      path: routePaths.transportLoad,
      label: 'Transport load',
      icon: TruckOutlined,
      allowedRoles: [UserRole.admin, UserRole.transport_manager],
    },
    {
      path: routePaths.distributionEfficiency,
      icon: AreaChartOutlined,
      label: 'Distribution efficiency',
      allowedRoles: [UserRole.admin, UserRole.transport_manager],
    },
    {
      path: routePaths.statisticProfit,
      icon: DollarOutlined,
      label: 'Profit',
      allowedRoles: [UserRole.admin],
    },
    {
      path: routePaths.whPerformance,
      icon: ShopOutlined,
      label: 'WH Performance',
      allowedRoles: [UserRole.admin],
    },
    {
      path: routePaths.overdue,
      icon: ClockCircleOutlined,
      label: 'Overdue',
      allowedRoles: [UserRole.admin],
    },
    {
      path: routePaths.futurePayments,
      icon: CreditCardOutlined,
      label: 'Future payments',
      allowedRoles: [UserRole.admin],
    },
  ];

  const itemsArr: IMenuItem[] = [
    {
      path: routePaths.home,
      label: 'Insights',
      icon: PieChartOutlined,
    },
    { path: routePaths.clientsList, label: 'Clients', icon: TeamOutlined },
    { path: routePaths.orders, label: 'Orders', icon: SolutionOutlined },
    { path: routePaths.cargos, label: 'Cargos', icon: CodeSandboxOutlined },
    { path: routePaths.trips, label: 'Trips', icon: CarOutlined },
    {
      path: routePaths.invoices,
      label: 'Invoices',
      icon: DiffOutlined,
      allowedRoles: [UserRole.admin, UserRole.assistant, UserRole.accountant],
    },
    {
      path: routePaths.expenses,
      label: 'Expenses',
      icon: DollarOutlined,
      allowedRoles: [UserRole.admin, UserRole.assistant, UserRole.accountant],
    },
    { path: routePaths.whTasks, label: 'WH Tasks', icon: HomeOutlined },
    { path: routePaths.whInfo, label: 'WH Info', icon: ShopOutlined },
    {
      path: 'statistics',
      label: 'Statistics',
      icon: BarChartOutlined,
      allowedRoles: [UserRole.admin, UserRole.assistant, UserRole.transport_manager],
      children: statisticsMenuItems,
    },
    { path: 'company', label: 'Our company', icon: SolutionOutlined, children: companyMenuItems },
  ];

  const newItems = useGetItems(itemsArr);

  return (
    <Layout.Sider theme='light' collapsed={collapsed} collapsible onCollapse={(value) => setCollapsed(value)}>
      <Menu defaultSelectedKeys={[`/${basePath}`]} mode='inline' items={newItems} />
    </Layout.Sider>
  );
};
